/**
 * Converts various YouTube URL formats to embeddable URLs.
 *
 * Supports:
 * - Standard watch URLs: https://www.youtube.com/watch?v=VIDEO_ID
 * - Short URLs: https://youtu.be/VIDEO_ID
 * - Already embed URLs: https://www.youtube.com/embed/VIDEO_ID (pass-through)
 *
 * @param url - The YouTube URL to convert
 * @returns The embed URL, or the original URL if not a recognized YouTube format
 */
export function convertYouTubeToEmbed(url: string): string {
  if (!url) return url;

  // Already an embed URL - return as-is
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  // Standard YouTube watch URL: https://www.youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
  );
  if (watchMatch) {
    return `https://www.youtube.com/embed/${watchMatch[1]}`;
  }

  // Short YouTube URL: https://youtu.be/VIDEO_ID
  const shortMatch = url.match(
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/,
  );
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}`;
  }

  // Not a recognized YouTube URL format - return original
  return url;
}

/**
 * Checks if a URL is a video URL (YouTube, Vimeo, or embed).
 *
 * @param url - The URL to check
 * @returns true if the URL appears to be a video URL
 */
export function isVideoUrl(url: string): boolean {
  if (!url) return false;

  return (
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('vimeo.com') ||
    url.includes('embed')
  );
}
